import { useCallback, useRef, useState } from 'react'
import { buildExport, countLiveEntries, downloadExport } from '@/core/services/exportService'
import {
  eraseAllData,
  ImportError,
  importBundle,
  parseBundle,
  previewImport,
  readFileAsText,
  resetPullCursor,
  type ImportMode,
  type ImportPreview,
} from '@/core/services/importService'
import type { ExportBundle } from '@/core/services/exportService'
import { updateSettings } from '@/core/repository/settingsRepository'
import { seedIfEmpty } from '@/db/seed'
import { logger } from '@/core/logging/logger'
import './backup.css'

interface BackupPanelProps {
  onClose: () => void
  onChanged: () => void
}

type Status =
  | { kind: 'idle' }
  | { kind: 'busy'; message: string }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string }

interface Staged {
  bundle: ExportBundle
  preview: ImportPreview
  mode: ImportMode
  filename: string
}

/**
 * Export and import.
 *
 * Sync already protects against a lost device. This protects against the one
 * thing sync cannot: deleting something by mistake and having that deletion
 * propagate everywhere. Anything destructive takes an automatic export first.
 *
 * Importing is two steps -- inspect, then apply. Merge is very often a correct
 * no-op (re-importing a file you just exported changes nothing), and without
 * showing what a file WOULD do that is indistinguishable from the feature
 * being broken.
 */
export function BackupPanel({ onClose, onChanged }: BackupPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<Status>({ kind: 'idle' })
  const [pendingMode, setPendingMode] = useState<ImportMode>('merge')
  const [staged, setStaged] = useState<Staged | null>(null)
  const [confirmErase, setConfirmErase] = useState(false)

  const handleExport = useCallback(() => {
    setStatus({ kind: 'busy', message: 'Preparing export…' })
    void downloadExport()
      .then(({ rows, method }) => {
        if (method === 'cancelled') {
          setStatus({ kind: 'idle' })
          return
        }
        void updateSettings({ lastExportAt: Date.now() })
        setStatus({
          kind: 'done',
          message:
            method === 'shared'
              ? `${rows} rows ready. Choose "Save to Files" to keep it.`
              : `Exported ${rows} rows.`,
        })
      })
      .catch((err: unknown) => {
        logger.error('db', 'Export failed', err)
        setStatus({ kind: 'error', message: 'Could not build the export.' })
      })
  }, [])

  const pickFile = useCallback((mode: ImportMode) => {
    setPendingMode(mode)
    setStaged(null)
    fileRef.current?.click()
  }, [])

  /** Step one: read and inspect, apply nothing. */
  const inspectFile = useCallback(
    async (file: File) => {
      setStatus({ kind: 'busy', message: 'Reading file…' })

      try {
        const text = await readFileAsText(file)

        if (!text.trim()) {
          throw new ImportError(
            'That file came back empty. If it lives in iCloud Drive, open it in the Files app once so it downloads, then try again.',
          )
        }

        const bundle = parseBundle(text)
        const preview = await previewImport(bundle)

        setStaged({ bundle, preview, mode: pendingMode, filename: file.name })
        setStatus({ kind: 'idle' })
      } catch (err: unknown) {
        const message = err instanceof ImportError ? err.message : 'Could not read that file.'
        logger.error('db', 'Import inspection failed', err)
        setStatus({ kind: 'error', message })
      }
    },
    [pendingMode],
  )

  /** Step two: apply what was inspected. */
  const applyStaged = useCallback(async () => {
    if (!staged) return

    try {
      if (staged.mode === 'replace') {
        setStatus({ kind: 'busy', message: 'Backing up current data first…' })
        const backup = await downloadExport()
        if (backup.method === 'cancelled') {
          setStatus({
            kind: 'error',
            message: 'Cancelled. Nothing was changed — the backup was not saved.',
          })
          return
        }
        await eraseAllData()
      }

      setStatus({ kind: 'busy', message: 'Importing…' })
      const result = await importBundle(staged.bundle, staged.mode)
      onChanged()
      setStaged(null)

      const changed = result.inserted + result.updated
      setStatus({
        kind: 'done',
        message:
          staged.mode === 'replace'
            ? `Restored ${result.inserted} rows.`
            : changed === 0
              ? `Nothing to change — this device already has everything in that file, or newer versions of it.`
              : `Merged: ${result.inserted} added, ${result.updated} updated.`,
      })
    } catch (err: unknown) {
      const message = err instanceof ImportError ? err.message : 'Could not import that file.'
      logger.error('db', 'Import failed', err)
      setStatus({ kind: 'error', message })
    }
  }, [staged, onChanged])

  const handleErase = useCallback(() => {
    if (!confirmErase) {
      setConfirmErase(true)
      return
    }

    setStatus({ kind: 'busy', message: 'Backing up before erasing…' })
    void downloadExport()
      .then(async (backup) => {
        if (backup.method === 'cancelled') {
          setConfirmErase(false)
          setStatus({ kind: 'error', message: 'Cancelled. Nothing was erased.' })
          return
        }
        await eraseAllData()
        // Recreate the starter catalogue immediately so the picker is not left
        // empty and unusable. Seed rows use fixed ids, so if the server also
        // holds a catalogue the two collapse into one on the next sync rather
        // than doubling up.
        await seedIfEmpty()
        onChanged()
        setConfirmErase(false)
        setStatus({
          kind: 'done',
          message:
            'Erased, and a backup was saved. Sync will pull your history back from the server; if there is none, import your backup file.',
        })
      })
      .catch((err: unknown) => {
        logger.error('db', 'Erase failed', err)
        setStatus({ kind: 'error', message: 'Could not erase.' })
      })
  }, [confirmErase, onChanged])

  const handleRecover = useCallback(() => {
    setStatus({ kind: 'busy', message: 'Asking the server for everything…' })
    void resetPullCursor()
      .then(() => {
        onChanged()
        setStatus({
          kind: 'done',
          message: 'Cursor reset. The next sync will re-download your full history.',
        })
      })
      .catch((err: unknown) => {
        logger.error('sync', 'Could not reset the pull cursor', err)
        setStatus({ kind: 'error', message: 'Could not reset the sync cursor.' })
      })
  }, [onChanged])

  const handleSummary = useCallback(() => {
    void buildExport()
      .then((bundle) =>
        setStatus({ kind: 'done', message: `${countLiveEntries(bundle)} blocks tracked here.` }),
      )
      .catch(() => setStatus({ kind: 'error', message: 'Could not read the database.' }))
  }, [])

  return (
    <div className="bk-sheet" role="dialog" aria-label="Backup and export">
      <header className="bk-head">
        <h2 className="bk-title">Backup</h2>
        <button className="bk-close" onClick={onClose}>
          Done
        </button>
      </header>

      {/* Pinned under the header, not inside the scroll area: a result message
          that scrolls out of view reads as nothing having happened. */}
      {status.kind !== 'idle' ? (
        <p className={`bk-status bk-status--${status.kind}`} role="status">
          {status.message}
        </p>
      ) : null}

      <div className="bk-body scroll-y">
        {staged ? (
          <section className="bk-section bk-staged">
            <h3 className="bk-label">Ready to import</h3>
            <p className="bk-file-name">{staged.filename}</p>

            <dl className="bk-stats">
              <Stat label="Blocks in file" value={staged.preview.fileEntries} />
              <Stat label="Blocks here now" value={staged.preview.localEntries} />
              <Stat label="Will be added" value={staged.preview.wouldInsert} />
              <Stat label="Will be updated" value={staged.preview.wouldUpdate} />
              <Stat label="Already current" value={staged.preview.wouldSkip} />
            </dl>

            {staged.mode === 'merge' && staged.preview.blockedByDeletion > 0 ? (
              <p className="bk-note">
                {staged.preview.blockedByDeletion} block
                {staged.preview.blockedByDeletion === 1 ? '' : 's'} in this file were deleted here
                afterwards. Merge keeps the deletion, because it is the more recent fact. Use{' '}
                <strong>Replace everything</strong> if you want the file to win outright.
              </p>
            ) : null}

            {staged.mode === 'merge' &&
            staged.preview.wouldInsert + staged.preview.wouldUpdate === 0 ? (
              <p className="bk-note">
                Nothing would change. This device already has everything in that file, or newer
                versions of it — which is exactly what you want to see after re-importing a fresh
                backup.
              </p>
            ) : null}

            <button
              className={`bk-btn ${staged.mode === 'replace' ? 'bk-btn--danger' : 'bk-btn--primary'}`}
              onClick={() => void applyStaged()}
            >
              {staged.mode === 'replace' ? 'Erase and restore' : 'Apply merge'}
            </button>
            <button className="bk-btn" onClick={() => setStaged(null)}>
              Cancel
            </button>
          </section>
        ) : null}

        <section className="bk-section">
          <h3 className="bk-label">Export</h3>
          <p className="bk-muted">
            Saves everything as a JSON file, including deletions. Keep it somewhere other than
            this device.
          </p>
          <button className="bk-btn bk-btn--primary" onClick={handleExport}>
            Export to file
          </button>
          <button className="bk-btn" onClick={handleSummary}>
            What is stored?
          </button>
        </section>

        <section className="bk-section">
          <h3 className="bk-label">Import</h3>
          <p className="bk-muted">
            <strong>Merge</strong> keeps whichever copy of each block is newer, so importing an
            old file can never undo recent work — including deletions. <strong>Replace</strong>{' '}
            wipes this device first, so the file wins outright. Both show you what will happen
            before anything changes.
          </p>
          <button className="bk-btn" onClick={() => pickFile('merge')}>
            Merge from file
          </button>
          <button className="bk-btn bk-btn--danger" onClick={() => pickFile('replace')}>
            Replace everything
          </button>

          {/*
            NO `accept` attribute, deliberately.

            iOS does not recognise .json as a type it can filter on, so an
            accept list greys out every export file in the Files picker and the
            button appears to do nothing. The contents are validated instead.

            Positioned off-screen rather than `display: none`: a display-none
            input cannot be opened programmatically in some browsers.
          */}
          <input
            ref={fileRef}
            type="file"
            className="bk-file"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) void inspectFile(file)
            }}
          />
        </section>

        <section className="bk-section">
          <h3 className="bk-label">Recover</h3>
          <p className="bk-muted">
            If this device is missing history that your other device still has, this asks the
            server for everything again from the beginning. Safe to run at any time — nothing is
            deleted.
          </p>
          <button className="bk-btn" onClick={handleRecover}>
            Re-download everything from server
          </button>
        </section>

        <section className="bk-section">
          <h3 className="bk-label">Danger</h3>
          <p className="bk-muted">
            Erases everything on this device. A backup is saved first, automatically. Your other
            device and the server are untouched, and will sync it back.
          </p>
          <button className="bk-btn bk-btn--danger" onClick={handleErase}>
            {confirmErase ? 'Tap again to confirm' : 'Erase all local data'}
          </button>
        </section>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bk-stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
